import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CvCategory, listCategories } from "features/cv-processor/api/CvCategoryApi";
import Modal from "shared/Modal";
import Button from "shared/Button";
import Spinner from "shared/Spinner";

interface Props {
  defaultTitle: string;
  onSave: (title: string, categoryId: number | null) => Promise<void>;
  onClose: () => void;
}

const saveAsNewCvSchema = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  categoryId: Yup.mixed<number | "">(),
});

/** Small modal for saving an enhanced CV (from CV Processor's own enhancement
 * flow or from a job card's "Enhanced CV" view) as a brand-new regular CV. */
export default function SaveAsNewCvModal({ defaultTitle, onSave, onClose }: Props) {
  const [categories, setCategories] = useState<CvCategory[]>([]);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {});
  }, []);

  const activeCategories = categories.filter((c) => c.is_active && !c.is_system && !c.is_skill_family);

  const formik = useFormik({
    initialValues: { title: defaultTitle, categoryId: "" as number | "" },
    validationSchema: saveAsNewCvSchema,
    onSubmit: async (vals, { setSubmitting }) => {
      setApiError("");
      try {
        await onSave(vals.title.trim(), vals.categoryId === "" ? null : Number(vals.categoryId));
      } catch (e: any) {
        setApiError(e?.response?.data?.detail ?? e?.message ?? "Could not save. Please try again.");
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal size="md" onClick={onClose} closeButtonCLick={onClose} modalClassName="min-h-0">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground font-inter">Save as new CV</h2>
      </div>

      <form onSubmit={formik.handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">CV title</label>
              <input
                type="text"
                {...formik.getFieldProps("title")}
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
              {formik.touched.title && formik.errors.title && (
                <p className="mt-1 text-xs text-red-400 font-inter">{formik.errors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
                CV Category <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <select
                name="categoryId"
                value={formik.values.categoryId}
                onChange={(e) => formik.setFieldValue("categoryId", e.target.value === "" ? "" : Number(e.target.value))}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <option value="">— No category —</option>
                {activeCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {apiError && <p className="text-xs text-red-400 font-inter">{apiError}</p>}
          </div>

          <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
            <Button
              disableDefaultStyles
              type="button"
              onClick={onClose}
              disabled={formik.isSubmitting}
              buttonClasses="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors disabled:opacity-50"
            >
              Cancel
            </Button>
            <Button
              disableDefaultStyles
              type="submit"
              disabled={formik.isSubmitting || !formik.values.title.trim()}
              buttonClasses="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
            >
              {formik.isSubmitting ? <Spinner size="sm" className="text-foreground" /> : "Save"}
            </Button>
          </div>
        </form>
    </Modal>
  );
}
